import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  CardFooter,
  Button, 
  OutlineButton, 
  FormGroup,
  Label,
  Input,
  ErrorText
} from '../../styles/StyledComponents';
import apiService from '../../services/api';

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  padding: 1rem;
`;

const FormCard = styled(Card)`
  max-width: 500px;
  width: 100%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  
  h1 {
    color: var(--primary-color);
    margin: 0;
    font-size: 2rem;
    font-weight: 700;
  }
  
  p {
    color: var(--dark-gray);
    margin: 0.5rem 0 0 0;
  }
`;

const SuccessMessage = styled.div`
  background: var(--success-color);
  color: white;
  padding: 1rem;
  border-radius: var(--border-radius);
  margin-bottom: 1rem;
  text-align: center;
`;

const ErrorMessage = styled.div`
  background: var(--danger-color);
  color: white;
  padding: 1rem;
  border-radius: var(--border-radius);
  margin-bottom: 1rem;
  text-align: center;
`;

const InfoBox = styled.div`
  background: var(--light-gray);
  padding: 1rem;
  border-radius: var(--border-radius);
  margin-bottom: 1rem;
  
  h4 {
    margin: 0 0 0.5rem 0;
    color: var(--secondary-color);
  }
  
  p {
    margin: 0;
    color: var(--dark-gray);
    font-size: 0.9rem;
  }
`;

interface FormErrors {
  password?: string;
  confirmPassword?: string;
}

const SetPassword: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [userInfo, setUserInfo] = useState<{
    name: string;
    email: string;
    role: string;
  } | null>(null);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenExpired, setTokenExpired] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (token) {
      // Validate the token with the backend and get user info
      const validateToken = async () => {
        try {
          setLoading(true);
          
          // Call backend to validate token and get user info
          const response = await apiService.validateToken(token);
          
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.user) {
              setTokenValid(true);
              setUserInfo({
                name: data.user.name,
                email: data.user.email,
                role: data.user.role
              });
            } else {
              setTokenValid(false);
              setError('Invalid or expired activation token');
            }
          } else {
            setTokenValid(false);
            setError('Invalid activation token');
          }
        } catch (error: any) {
          console.error('Token validation error:', error);
          setTokenValid(false);
          setError(error.message || 'Invalid activation token');
        } finally {
          setLoading(false);
        }
      };
      
      validateToken();
    } else {
      setLoading(false);
      setTokenValid(false);
      setError('No activation token provided');
    }
  }, [token]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when field is edited
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Call the backend API to set the password
      const response = await apiService.setPassword(token!, formData.password);
      
      if (response.success) {
        setSuccess(true);
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(response.message || 'Failed to set password');
      }
      
    } catch (error: any) {
      console.error('Error setting password:', error);
      setError(error.message || 'Failed to set password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <FormCard>
          <CardBody>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div>Validating activation link...</div>
            </div>
          </CardBody>
        </FormCard>
      </Container>
    );
  }

  if (!tokenValid || error) {
    return (
      <Container>
        <FormCard>
          <CardHeader>
            <Logo>
              <h1>Funds Request System</h1>
              <p>Account Activation</p>
            </Logo>
          </CardHeader>
          <CardBody>
            <ErrorMessage>
              <h4>Invalid Activation Link</h4>
              <p>{error || 'The activation link is invalid or has already been used. Please contact your administrator for assistance.'}</p>
            </ErrorMessage>
            
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <OutlineButton as={Link} to="/login">
                Go to Login
              </OutlineButton>
            </div>
          </CardBody>
        </FormCard>
      </Container>
    );
  }

  if (success) {
    return (
      <Container>
        <FormCard>
          <CardHeader>
            <Logo>
              <h1>Funds Request System</h1>
              <p>Account Activation</p>
            </Logo>
          </CardHeader>
          <CardBody>
            <SuccessMessage>
              <h4>Password Set Successfully!</h4>
              <p>Your account has been activated and your password has been set. You will be redirected to the login page shortly.</p>
            </SuccessMessage>
            
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <Button as={Link} to="/login">
                Go to Login Now
              </Button>
            </div>
          </CardBody>
        </FormCard>
      </Container>
    );
  }

  return (
    <Container>
      <FormCard>
        <CardHeader>
          <Logo>
            <h1>Funds Request System</h1>
            <p>Set Your Password</p>
          </Logo>
        </CardHeader>
        
        <CardBody>
          {userInfo && (
            <InfoBox>
              <h4>Welcome, {userInfo.name}!</h4>
              <p>You are setting up your account as a <strong>{userInfo.role}</strong>.</p>
              <p>Please create a strong password to secure your account.</p>
            </InfoBox>
          )}
          
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="password">New Password *</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                className={errors.password ? 'error' : ''}
                placeholder="Enter your new password"
              />
              {errors.password && <ErrorText>{errors.password}</ErrorText>}
              <small style={{ color: 'var(--dark-gray)', marginTop: '0.25rem', display: 'block' }}>
                Password must be at least 8 characters with uppercase, lowercase, and number
              </small>
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="confirmPassword">Confirm Password *</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={errors.confirmPassword ? 'error' : ''}
                placeholder="Confirm your new password"
              />
              {errors.confirmPassword && <ErrorText>{errors.confirmPassword}</ErrorText>}
            </FormGroup>
            
            <CardFooter style={{ padding: '1rem 0 0 0', border: 'none' }}>
              <Button type="submit" disabled={submitting} style={{ width: '100%' }}>
                {submitting ? 'Setting Password...' : 'Set Password & Activate Account'}
              </Button>
            </CardFooter>
          </form>
        </CardBody>
      </FormCard>
    </Container>
  );
};

export default SetPassword; 